import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { useApi } from '../hooks/useApi.js';
import { useAuth } from '../context/AuthContext.jsx';
import { dashboardApi, attendanceApi, holidayApi } from '../api/endpoints.js';
import {
  PageHeader, Card, StatTile, Badge, Avatar, AsyncContent,
  DataTable, Button, EmptyState,
} from '../components/ui.jsx';
import { fmt, inrCompact, dayjs } from '../utils/format.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

/** Chart colours read from the CSS tokens so both themes stay consistent. */
function token(name, fallback) {
  if (typeof window === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, can } = useAuth();

  const { data, loading, error, reload } = useApi(() => dashboardApi.admin(), []);
  const { data: trend } = useApi(() => dashboardApi.attendanceTrend(14), []);
  const { data: today } = useApi(() => attendanceApi.today(), []);
  const { data: holidays } = useApi(() => holidayApi.upcoming(3), []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <>
      <PageHeader
        title={`${greeting}, ${user?.name?.split(' ')[0] ?? 'there'}`}
        subtitle={dayjs().format('dddd, D MMMM YYYY')}
        actions={
          can('attendance.view.all') && (
            <Button variant="secondary" onClick={() => navigate('/attendance')}>
              Attendance register
            </Button>
          )
        }
      />

      <AsyncContent loading={loading} error={error} onRetry={reload}>
        {data && (
          <>
            {/* ------------------------- headline tiles ------------------- */}
            <div className="grid grid--4" style={{ marginBottom: 16 }}>
              <StatTile
                icon="👥"
                label="Headcount"
                value={data.headcount.total}
                hint={`${data.headcount.newThisMonth} joined this month`}
                onClick={can('employee.view.all') ? () => navigate('/employees') : undefined}
              />
              <StatTile
                icon="✅"
                label="Present today"
                value={data.todayAttendance.present}
                hint={`${fmt.percent(data.todayAttendance.attendanceRate)} of headcount`}
                tone="success"
                onClick={() => navigate('/attendance')}
              />
              <StatTile
                icon="🌴"
                label="On leave today"
                value={data.onLeaveToday}
                hint={`${data.todayAttendance.absent} absent`}
                tone="info"
                onClick={can('leave.view.all') ? () => navigate('/leave') : undefined}
              />
              <StatTile
                icon="⏳"
                label="Awaiting approval"
                value={data.pending.leaveRequests + data.pending.regularizations}
                hint={`${data.pending.leaveRequests} leave · ${data.pending.regularizations} attendance`}
                tone={data.pending.leaveRequests + data.pending.regularizations > 0 ? 'warning' : 'default'}
                onClick={() => navigate('/leave?status=PENDING')}
              />
            </div>

            {/* ---------------- things that need attention ---------------- */}
            {(data.pending.mispunchDays > 0 || data.devices.some((d) => !d.isOnline)) && (
              <Card title="Needs attention" className="card--attention">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {data.pending.mispunchDays > 0 && (
                    <div className="note note--warning">
                      <strong>{data.pending.mispunchDays}</strong> attendance day(s) this month have a missing punch.{' '}
                      <a href="/attendance?hasMispunch=true">Review them</a> — they will otherwise be paid on incomplete data.
                    </div>
                  )}
                  {data.devices.filter((d) => !d.isOnline).map((device) => (
                    <div key={device.name} className="note note--danger">
                      Punch device <strong>{device.name}</strong> has not reported since{' '}
                      {device.lastSeenAt ? fmt.fromNow(device.lastSeenAt) : 'ever'}. Attendance may be incomplete.
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <div className="grid grid--2">
              {/* ---------------------- attendance trend ------------------ */}
              <Card title="Attendance — last 14 days">
                {trend?.days?.length ? (
                  <div className="chart-box">
                    <Line
                      data={{
                        labels: trend.days.map((d) => dayjs(d.dateKey).format('D MMM')),
                        datasets: [
                          {
                            label: 'Present',
                            data: trend.days.map((d) => d.present),
                            borderColor: token('--success', '#047857'),
                            backgroundColor: 'rgba(4, 120, 87, 0.12)',
                            fill: true,
                            tension: 0.3,
                          },
                          {
                            label: 'Absent',
                            data: trend.days.map((d) => d.absent),
                            borderColor: token('--danger', '#b91c1c'),
                            backgroundColor: 'transparent',
                            tension: 0.3,
                          },
                          {
                            label: 'On leave',
                            data: trend.days.map((d) => d.onLeave),
                            borderColor: token('--info', '#0369a1'),
                            backgroundColor: 'transparent',
                            tension: 0.3,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } },
                        scales: {
                          y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: token('--border', '#e5e7eb') } },
                          x: { grid: { display: false } },
                        },
                      }}
                    />
                  </div>
                ) : (
                  <EmptyState icon="📈" title="No attendance data yet" message="It appears once the punch device starts reporting." />
                )}
              </Card>

              {/* -------------------- department split -------------------- */}
              <Card title="Headcount by department">
                {data.departmentSplit?.length ? (
                  <div className="chart-box">
                    <Doughnut
                      data={{
                        labels: data.departmentSplit.map((d) => d.name),
                        datasets: [
                          {
                            data: data.departmentSplit.map((d) => d.count),
                            backgroundColor: [
                              '#2563eb', '#0891b2', '#059669', '#d97706',
                              '#dc2626', '#7c3aed', '#db2777', '#65a30d',
                            ],
                            borderWidth: 0,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '62%',
                        plugins: { legend: { position: 'right', labels: { boxWidth: 10, font: { size: 11 } } } },
                      }}
                    />
                  </div>
                ) : (
                  <EmptyState icon="🏢" title="No departments configured" />
                )}
              </Card>
            </div>

            <div className="grid grid--2">
              {/* -------------------------- payroll ----------------------- */}
              {data.payroll && can('payroll.view.all') && (
                <Card
                  title="Latest payroll"
                  actions={<Button size="sm" variant="secondary" onClick={() => navigate('/payroll')}>Open payroll</Button>}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 650 }}>{fmt.month(data.payroll.period)}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
                        Pay date {fmt.date(data.payroll.payDate)}
                      </div>
                    </div>
                    <Badge status={data.payroll.status} />
                  </div>

                  <div className="grid grid--3" style={{ gap: 10 }}>
                    <StatTile label="Employees" value={data.payroll.totals?.employeeCount ?? 0} />
                    <StatTile label="Net payable" value={inrCompact(data.payroll.totals?.netPayable)} tone="success" />
                    <StatTile label="Total cost" value={inrCompact(data.payroll.totals?.ctcCost)} />
                  </div>
                </Card>
              )}

              {/* ----------------------- upcoming holidays ---------------- */}
              <Card
                title="Upcoming holidays"
                actions={<Button size="sm" variant="ghost" onClick={() => navigate('/holidays')}>View calendar</Button>}
              >
                {holidays?.length ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {holidays.map((h) => (
                      <div key={h._id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 46, textAlign: 'center', padding: '5px 0',
                          background: 'var(--purple-soft)', color: 'var(--purple)',
                          borderRadius: 8, lineHeight: 1.15,
                        }}>
                          <div style={{ fontSize: 17, fontWeight: 700 }}>{dayjs(h.date).format('DD')}</div>
                          <div style={{ fontSize: 10, fontWeight: 600 }}>{dayjs(h.date).format('MMM')}</div>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{h.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
                            {dayjs(h.date).format('dddd')} · {h.daysAway === 0 ? 'Today' : `in ${h.daysAway} day${h.daysAway === 1 ? '' : 's'}`}
                          </div>
                        </div>
                        {h.isOptional && <Badge tone="info">Optional</Badge>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon="📅" title="No upcoming holidays" message="Publish the year's calendar from the Holidays page." />
                )}
              </Card>
            </div>

            {/* --------------------- who is in right now ------------------ */}
            {today?.late?.length > 0 && (
              <Card title={`Late arrivals today (${today.late.length})`}>
                <DataTable
                  columns={[
                    {
                      key: 'employee',
                      header: 'Employee',
                      render: (row) => (
                        <div className="cell-primary">
                          <Avatar name={row.employee?.fullName} src={row.employee?.avatarUrl} size={30} />
                          <div className="cell-primary__text">
                            <span className="cell-primary__title">{row.employee?.fullName}</span>
                            <span className="cell-primary__sub">{row.employee?.employeeCode}</span>
                          </div>
                        </div>
                      ),
                    },
                    { key: 'firstIn', header: 'Punched in', render: (row) => fmt.time(row.firstIn) },
                    {
                      key: 'late',
                      header: 'Late by',
                      align: 'right',
                      render: (row) => <Badge tone="warning">{fmt.duration(row.lateByMinutes)}</Badge>,
                    },
                  ]}
                  rows={today.late.slice(0, 8)}
                />
              </Card>
            )}
          </>
        )}
      </AsyncContent>
    </>
  );
}
